import React from 'react';
import Icon from 'components/AppIcon';

const DIETARY_PREFS = [
  { id: 'no_restriction', label: 'No Restriction', icon: 'UtensilsCrossed' },
  { id: 'vegetarian', label: 'Vegetarian', icon: 'Leaf' },
  { id: 'vegan', label: 'Vegan', icon: 'Sprout' },
  { id: 'keto', label: 'Keto', icon: 'Beef' },
  { id: 'paleo', label: 'Paleo', icon: 'Drumstick' },
  { id: 'gluten_free', label: 'Gluten-Free', icon: 'WheatOff' },
  { id: 'dairy_free', label: 'Dairy-Free', icon: 'MilkOff' },
];

const ALLERGIES = ['Nuts', 'Dairy', 'Gluten', 'Eggs', 'Shellfish', 'Soy'];

const MEALS_OPTIONS = [2, 3, 4, '5+'];

const CALORIE_SUGGESTIONS = {
  lose_weight: { min: 1400, max: 1800, label: 'Deficit for weight loss' },
  build_muscle: { min: 2500, max: 3200, label: 'Surplus for muscle gain' },
  improve_endurance: { min: 2000, max: 2800, label: 'Maintenance + cardio fuel' },
  increase_flexibility: { min: 1800, max: 2200, label: 'Balanced maintenance' },
  general_fitness: { min: 1800, max: 2400, label: 'General maintenance' },
  sports_performance: { min: 2800, max: 3500, label: 'High performance fuel' },
};

export default function DietStep({ data, onChange, goalData }) {
  const handleChange = (field, value) => onChange({ ...data, [field]: value });

  const toggleAllergy = (allergy) => {
    const current = data?.allergies || [];
    const updated = current?.includes(allergy) ? current?.filter((a) => a !== allergy) : [...current, allergy];
    handleChange('allergies', updated);
  };

  const suggestion = CALORIE_SUGGESTIONS?.[goalData?.primaryGoal] || CALORIE_SUGGESTIONS?.general_fitness;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Diet Plan Intake</h2>
        <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Help us understand your dietary needs for a personalized nutrition plan.</p>
      </div>
      {/* Dietary Preference */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">Dietary Preference</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {DIETARY_PREFS?.map((pref) => {
            const isSelected = data?.dietaryPreference === pref?.id;
            return (
              <button
                key={pref?.id}
                type="button"
                onClick={() => handleChange('dietaryPreference', pref?.id)}
                className={`flex flex-col items-center gap-2 p-3 rounded-[var(--radius-md)] border-2 transition-all duration-200 ${
                  isSelected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/8 shadow-sm'
                    : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 bg-[var(--color-card)]'
                }`}
              >
                <Icon
                  name={pref?.icon}
                  size={18}
                  color={isSelected ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                  strokeWidth={2}
                />
                <span className={`text-xs font-medium font-[var(--font-caption)] text-center leading-tight ${
                  isSelected ? 'text-[var(--color-primary)]' : 'text-[var(--color-foreground)]'
                }`}>{pref?.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Food Allergies */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">
          Food Allergies <span className="text-[var(--color-muted-foreground)] font-normal">(select all that apply)</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {ALLERGIES?.map((allergy) => {
            const isSelected = data?.allergies?.includes(allergy);
            return (
              <button
                key={allergy}
                type="button"
                onClick={() => toggleAllergy(allergy)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium font-[var(--font-caption)] border transition-all duration-200 ${
                  isSelected
                    ? 'border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]'
                    : 'border-[var(--color-border)] text-[var(--color-foreground)] hover:border-[var(--color-destructive)]/40 bg-[var(--color-card)]'
                }`}
              >
                {isSelected && <Icon name="AlertTriangle" size={12} color="var(--color-destructive)" />}
                {allergy}
              </button>
            );
          })}
        </div>
      </div>
      {/* Calorie Goal */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-1.5">Daily Calorie Goal</label>
        {goalData?.primaryGoal && (
          <div className="flex items-center gap-2 mb-2 p-2.5 rounded-[var(--radius-sm)] bg-[var(--color-primary)]/8 border border-[var(--color-primary)]/20">
            <Icon name="Lightbulb" size={14} color="var(--color-primary)" />
            <p className="text-xs text-[var(--color-primary)] font-[var(--font-caption)]">
              Suggested: <strong>{suggestion?.min}–{suggestion?.max} kcal/day</strong> — {suggestion?.label}
            </p>
          </div>
        )}
        <input
          type="number"
          value={data?.calorieGoal || ''}
          onChange={(e) => handleChange('calorieGoal', parseInt(e?.target?.value) || '')}
          placeholder={`e.g. ${suggestion?.min}`}
          min="800"
          max="6000"
          className="w-full px-3 py-2.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm font-[var(--font-caption)] bg-[var(--color-background)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)] transition-colors"
        />
      </div>
      {/* Meals per day */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">Meals Per Day</label>
        <div className="flex gap-3">
          {MEALS_OPTIONS?.map((m) => {
            const val = m === '5+' ? 5 : m;
            const isSelected = data?.mealsPerDay === val;
            return (
              <button
                key={m}
                type="button"
                onClick={() => handleChange('mealsPerDay', val)}
                className={`w-14 h-14 rounded-[var(--radius-md)] border-2 text-sm font-bold font-[var(--font-heading)] transition-all duration-200 ${
                  isSelected
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white shadow-sm'
                    : 'border-[var(--color-border)] text-[var(--color-foreground)] hover:border-[var(--color-primary)]/50 bg-[var(--color-card)]'
                }`}
              >
                {m}
              </button>
            );
          })}
        </div>
      </div>
      {/* Water Intake */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-3">
          Daily Water Goal: <span className="text-[var(--color-primary)]">{data?.waterGoal || 2.5}L</span>
        </label>
        <div className="flex items-center gap-3">
          <Icon name="Droplets" size={16} color="var(--color-muted-foreground)" />
          <input
            type="range"
            min="1"
            max="5"
            step="0.5"
            value={data?.waterGoal || 2.5}
            onChange={(e) => handleChange('waterGoal', parseFloat(e?.target?.value))}
            className="flex-1 h-2 rounded-full accent-[var(--color-primary)] cursor-pointer"
          />
          <Icon name="Waves" size={16} color="#0ea5e9" />
        </div>
        <div className="flex justify-between text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">
          <span>1L</span>
          <span>2.5L</span>
          <span>5L</span>
        </div>
      </div>
      {/* Supplements */}
      <div>
        <label className="block text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] mb-1.5">
          Current Supplements <span className="text-[var(--color-muted-foreground)] font-normal text-xs">(optional)</span>
        </label>
        <input
          type="text"
          value={data?.supplements || ''}
          onChange={(e) => handleChange('supplements', e?.target?.value)}
          placeholder="e.g. Whey protein, Creatine, Vitamin D..."
          className="w-full px-3 py-2.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm font-[var(--font-caption)] bg-[var(--color-background)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30 focus:border-[var(--color-primary)] transition-colors"
        />
      </div>
    </div>
  );
}
